/**
 * Who, other than us, may sell this cultivar.
 *
 * Two switches that look adjacent and are not the same decision:
 *
 *   PUBLISHED (the panel above) is a marketing decision. It puts the cultivar
 *   on the public site for growers to read about.
 *
 *   SUBLICENSABLE (here) is a commercial one. It decides whether anybody other
 *   than our own nursery may be listed as able to supply it.
 *
 * Neither implies the other. A cultivar can be published and orderable only
 * from us; it can be sublicensable to a handful of nurseries and never appear
 * on the website at all. They are kept apart on purpose — folding them into
 * one toggle makes "let people read about it" and "let eleven nurseries sell
 * it" the same click.
 *
 * THE TICK IS PER NURSERY, BY NAME. A nursery arrives on this list by having
 * signed a master agreement with us; it gets THIS cultivar only when somebody
 * ticks it here. There is deliberately no way to grant a whole tier at once —
 * "everyone in Group A" is one click that hands several companies rights to
 * something, and the licensor would find out by reading a catalogue.
 *
 * Naming the companies is the point. "Add to Group A" is an abstract act with
 * a concrete consequence; a checkbox beside "Example Nursery Pty Ltd" is the
 * same decision with the consequence written on it.
 *
 * THE TICK IS NOT WHAT ALLOWS. The master is: it has to be sent and SIGNED,
 * and only then may the nursery be selected here. So a nursery with an
 * unsigned master appears with no checkbox at all and says what it is waiting
 * for. Rendering a live checkbox beside a company that has agreed to nothing
 * invites a rights holder to grant a cultivar against a contract that does not
 * exist yet — and the save would refuse it anyway, which is a worse way to
 * find out. The server publishes `selectable` from the same expression it
 * enforces on write, so this screen cannot drift out of step with it.
 *
 * A nursery whose master has lapsed still appears, greyed and labelled, and
 * stays tickable so the grant can be cleared. It is kept visible because the
 * tick is still there — hiding the row would make the catalogue shrink with
 * nothing on screen to explain it.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Handshake, Loader2, AlertCircle, Building2, Home, Sprout, ExternalLink, Lock,
} from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import cultivarService, {
  type SublicensableNursery,
  type CultivarSuppliers,
} from '../../services/cultivar';
import type { Cultivar } from '../../types/cultivar';

interface SublicensingPanelProps {
  cultivar: Cultivar;
}

/**
 * What a master's status means for this screen, in the licensor's own terms.
 *
 * "pending_confirmation" is the one worth naming precisely: the agreement has
 * been sent and is sitting with the nursery unsigned. "Not active" would read
 * as a problem at our end and invite somebody to chase the wrong thing.
 */
function masterStateLabel(status: string): string {
  switch (status) {
    case 'active': return '';
    case 'pending_confirmation': return 'sent — awaiting their signature';
    case 'rejected': return 'declined by the nursery';
    case 'suspended': return 'suspended';
    case 'terminated': return 'terminated';
    case 'expired': return 'expired';
    default: return status;
  }
}

function SublicensingPanel({ cultivar }: SublicensingPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [isSublicensable, setIsSublicensable] = useState(false);
  const [nurseries, setNurseries] = useState<SublicensableNursery[]>([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [suppliers, setSuppliers] = useState<CultivarSuppliers | null>(null);

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await cultivarService.getCultivarSuppliers(cultivar.id);
      if (res.success && res.data) setSuppliers(res.data);
    } catch {
      // The preview failing to load is not a reason to block the controls.
    }
  }, [cultivar.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const subRes = await cultivarService.getCultivarSublicensing(cultivar.id);
        if (cancelled) return;
        if (subRes.success && subRes.data) {
          setIsSublicensable(!!subRes.data.is_sublicensable);
          setNurseries(subRes.data.nurseries);
          setSelectedOrgIds(
            subRes.data.nurseries.filter((n) => n.granted).map((n) => n.nursery_org_id)
          );
        }
      } catch {
        if (!cancelled) setError('Could not load sublicensing settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cultivar.id]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  // One save path for both controls. The API takes the complete set of
  // nurseries rather than a delta, because that is what this screen shows —
  // sending a delta would silently keep a tick the operator believes they
  // cleared.
  const persist = async (next: { is_sublicensable?: boolean; nursery_org_ids?: number[] }) => {
    setError(null);
    setSaving(true);
    try {
      const res = await cultivarService.setCultivarSublicensing(cultivar.id, next);
      if (res.success && res.data) {
        setIsSublicensable(!!res.data.is_sublicensable);
        setNurseries(res.data.nurseries);
        setSelectedOrgIds(
          res.data.nurseries.filter((n) => n.granted).map((n) => n.nursery_org_id)
        );
        setSavedAt(new Date().toISOString());
        await loadSuppliers();
      } else {
        setError(res.message || 'Failed to save.');
      }
    } catch {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const toggleNursery = (orgId: number) => {
    const next = selectedOrgIds.includes(orgId)
      ? selectedOrgIds.filter((n) => n !== orgId)
      : [...selectedOrgIds, orgId];
    setSelectedOrgIds(next);
    persist({ nursery_org_ids: next });
  };

  const sublicensed = suppliers?.suppliers.filter((s) => s.kind === 'sublicensed_nursery') ?? [];
  const house = suppliers?.suppliers.find((s) => s.kind === 'house_nursery');

  return (
    <Card className="mt-6">
      <CardContent className="p-6 sm:p-8">
        {/* The toggle sits beside the heading on a laptop and below it on a
            phone. Side by side at 320px the title block squeezes the switch's
            label off the right edge — it is 94px of unbreakable text with no
            room left to put it in. */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Handshake className="h-5 w-5 text-indigo-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Sublicensing</h3>
              <p className="text-sm text-neutral-600">
                Whether nurseries other than our own may be offered as suppliers of this
                cultivar. Separate from publishing it — a published cultivar can still be
                orderable from us alone.
              </p>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none flex-shrink-0">
            <input
              type="checkbox"
              checked={isSublicensable}
              onChange={(e) => {
                setIsSublicensable(e.target.checked);
                persist({ is_sublicensable: e.target.checked });
              }}
              disabled={saving || loading}
              className="h-5 w-5 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className={`text-sm font-medium ${isSublicensable ? 'text-indigo-700' : 'text-neutral-500'}`}>
              {isSublicensable ? 'Sublicensable' : 'Not sublicensable'}
            </span>
          </label>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-md bg-error-50 border border-error-200 p-3 text-sm text-error-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="mb-5">
              <h4 className="text-sm font-medium text-neutral-800 mb-1">
                Nurseries that may grow this cultivar
              </h4>
              <p className="text-xs text-neutral-500 mb-3">
                Everyone here has been issued a master sublicence by us. A nursery can be
                ticked once it has <strong className="font-medium text-neutral-700">signed</strong> that
                master — the signed agreement is what allows it to produce; the tick only says
                which cultivars it covers. Ticking one grants this cultivar and nothing else.
              </p>

              {nurseries.length === 0 ? (
                <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-600">
                  <p className="mb-2">
                    No nursery holds a master sublicence with you yet. A master is the agreement
                    that lets a nursery produce for you at all — issue one, and get it signed,
                    before there is anybody to tick here.
                  </p>
                  <Link to="/cultivar/sublicence-groups" className="text-indigo-700 font-medium hover:underline">
                    Set up groups and masters
                  </Link>
                </div>
              ) : (
                <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 overflow-hidden">
                  {nurseries.map((n) => {
                    const checked = selectedOrgIds.includes(n.nursery_org_id);
                    const live = n.master_status === 'active';
                    // `selectable` is the server's word, not a second copy of
                    // the rule. An older payload without it falls back to the
                    // same expression rather than rendering every row locked.
                    const selectable = n.selectable ?? (live || checked);
                    const state = masterStateLabel(n.master_status);

                    const Row = selectable ? 'label' : 'div';
                    return (
                      <Row
                        key={n.nursery_org_id}
                        className={`flex items-start gap-3 px-4 py-3 ${
                          !selectable ? 'bg-neutral-50' : checked ? 'bg-indigo-50 cursor-pointer' : 'hover:bg-neutral-50 cursor-pointer'
                        }`}
                      >
                        {selectable ? (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleNursery(n.nursery_org_id)}
                            disabled={saving}
                            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        ) : (
                          // No disabled checkbox. A greyed box still reads as
                          // "tick me once you work out what is wrong"; a lock
                          // says the decision is not available here at all,
                          // and the line below says where it is.
                          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className={`block text-sm font-medium break-words ${live ? 'text-neutral-900' : 'text-neutral-500'}`}>
                            {n.nursery_name}
                          </span>
                          <span className="block text-xs text-neutral-500">
                            Master #{n.master_license_id}
                            {n.group_code ? ` · group ${n.group_code}` : ''}
                            {/* A lapsed master is the reason a ticked nursery
                                is not on the website, so the row says it here
                                rather than leaving the preview to disagree with
                                the tick for no stated reason. */}
                            {!live && ` · master ${state}${checked ? ' — not currently offered' : ''}`}
                          </span>
                          {!selectable && (
                            <span className="block text-xs text-neutral-600 mt-1">
                              {n.master_status === 'pending_confirmation'
                                ? 'They can be given this cultivar once they sign. Nothing to do here until then.'
                                : 'A signed master is what allows a nursery to grow anything. Issue a new one to bring them back.'}
                            </span>
                          )}
                        </span>
                      </Row>
                    );
                  })}
                </div>
              )}
            </div>

            {/* The consequence, spelled out. Ticking a group is abstract; the
                list of companies that can now sell your cultivar is not. */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <h4 className="text-sm font-medium text-neutral-800 mb-2">
                What a customer will be offered
              </h4>

              <ul className="space-y-2">
                {house && (
                  <li className="flex items-start gap-2 text-sm text-neutral-800">
                    <Home className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                    <span className="min-w-0 break-words">
                      <strong>{house.name}</strong>
                      <span className="text-neutral-500"> — our own nursery, always offered</span>
                    </span>
                  </li>
                )}

                {!house && (
                  <li className="flex items-start gap-2 text-sm text-amber-900">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span className="min-w-0">
                      No house nursery is configured, so "order from us" will not appear.
                    </span>
                  </li>
                )}

                {sublicensed.map((s) => (
                  <li key={s.org_id} className="flex items-start gap-2 text-sm text-neutral-800">
                    <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-600" />
                    <span className="min-w-0 break-words">
                      <strong>{s.name}</strong>
                      <span className="text-neutral-500">
                        {s.group_code ? ` — master sublicence, group ${s.group_code}` : ' — master sublicence'}
                      </span>
                    </span>
                  </li>
                ))}

                {isSublicensable && sublicensed.length === 0 && (
                  <li className="flex items-start gap-2 text-sm text-neutral-600">
                    <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-neutral-400" />
                    <span className="min-w-0">
                      No ticked nursery currently holds a live master, so only our own nursery
                      will be listed.
                    </span>
                  </li>
                )}

                <li className="flex items-start gap-2 text-sm text-neutral-800">
                  <Sprout className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span className="min-w-0">
                    <strong>Propagate their own</strong>
                    <span className="text-neutral-500">
                      {' '}— always offered, and issues no licence. It reaches you as an enquiry
                      to price and set up by hand.
                    </span>
                  </span>
                </li>
              </ul>

              {!isSublicensable && (
                <p className="mt-3 text-xs text-neutral-500">
                  While this cultivar is not sublicensable, no nursery is listed however the
                  ticks are set. The ticks are kept, so turning it back on restores them.
                </p>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <Link
                to="/cultivar/sublicence-groups"
                className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline"
              >
                Manage groups and master agreements
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
              <div className="flex items-center gap-3">
                {saving && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
                  </span>
                )}
                {!saving && savedAt && (
                  <span className="text-xs text-neutral-500">Saved</span>
                )}
                <Button variant="outline" onClick={loadSuppliers} disabled={saving}>
                  Refresh preview
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default SublicensingPanel;
